import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DashboardShell } from "@/components/Shell";
import { MapPin, Plus, Trash2, Pencil, X, Check } from "lucide-react";

export default function AdminLocations() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | locationId
  const [form, setForm] = useState({ name: "", address: "", photo_url: "" });

  function load() { api.get("/locations").then((r) => setItems(r.data)); }
  useEffect(load, []);

  function startNew() { setEditing("new"); setForm({ name: "", address: "", photo_url: "" }); }
  function startEdit(l) { setEditing(l.id); setForm({ name: l.name, address: l.address, photo_url: l.photo_url }); }
  function cancel() { setEditing(null); setForm({ name: "", address: "", photo_url: "" }); }

  async function save() {
    if (editing === "new") await api.post("/locations", form);
    else await api.put(`/locations/${editing}`, form);
    cancel(); load();
  }

  async function remove(id) {
    if (!window.confirm("Location wirklich löschen?")) return;
    await api.delete(`/locations/${id}`);
    load();
  }

  return (
    <DashboardShell role="admin">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Verwaltung</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-2 flex items-center gap-3"><MapPin className="w-9 h-9 text-[#EBB300]" /> Locations</h1>
        </div>
        <button onClick={startNew} data-testid="admin-add-location"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold hover:bg-[#FAD02C] transition-colors">
          <Plus className="w-4 h-4" /> Neue Location
        </button>
      </div>

      {editing && (
        <div className="mb-6 bg-[#121216] border border-[#EBB300]/30 rounded-2xl p-6" data-testid="admin-location-form">
          <h3 className="font-bold mb-4">{editing === "new" ? "Neue Location" : "Location bearbeiten"}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-loc-name" />
            <Input label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="admin-loc-address" />
            <Input label="Foto-URL" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testid="admin-loc-photo" />
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={save} data-testid="admin-loc-save" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold text-sm"><Check className="w-4 h-4" /> Speichern</button>
            <button onClick={cancel} data-testid="admin-loc-cancel" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-white text-sm"><X className="w-4 h-4" /> Abbrechen</button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-locations-list">
        {items.map((l) => (
          <div key={l.id} className="bg-[#121216] border border-white/5 rounded-2xl overflow-hidden" data-testid={`admin-location-${l.id}`}>
            {l.photo_url && <img src={l.photo_url} alt={l.name} className="w-full h-32 object-cover" />}
            <div className="p-5">
              <h3 className="font-bold text-lg">{l.name}</h3>
              <p className="text-xs text-[#A1A1AA] mt-1">{l.address}</p>
              <div className="flex gap-2 mt-4">
                <button onClick={() => startEdit(l)} data-testid={`admin-edit-location-${l.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm">
                  <Pencil className="w-3.5 h-3.5" /> Bearbeiten
                </button>
                <button onClick={() => remove(l.id)} data-testid={`admin-delete-location-${l.id}`}
                  className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-[#EF4444]/10 hover:bg-[#EF4444]/20 text-[#EF4444]">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.15em] uppercase text-[#A1A1AA] mb-2 block">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-2.5 text-white focus:border-[#EBB300]" />
    </label>
  );
}
